const get_route_label_context = (route) => ({
  section: String(route.meta?.onboardingSection || 'Overview'),
  title: String(route.meta?.onboardingTitle || 'Command centre'),
  case_name: String(route.query.name || '').trim(),
  company_name: String(route.query.company || '').trim(),
  route_name: String(route.name || ''),
  application_id: route.params.applicationId || 'active'
});

const build_application_meta = ({ section, title, application_id }) =>
  `Route - ${section} / ${title} - Application ${application_id}`;

const build_default_panel_content = ({ section, title }) => ({
  summary_label: 'Briefing',
  summary_intro: `${section} is active and ${title.toLowerCase()} is ready for review.`,
  briefing_body: `Use the ${title.toLowerCase()} workspace to monitor onboarding progress, identify blockers early, and move from summary signals into the exact journey, gate or entity that needs action.`,
  briefing_meta: `Route - ${section} / ${title} - Live workspace`,
  suggested_questions: [
    `What should I review in ${title}?`,
    `Which blockers matter most in ${section}?`,
    'Which entities are closest to activation?',
    'What evidence needs attention today?',
    'What changed most recently?'
  ],
  recent_evidence: [
    { title: `${title} workspace`, meta: `${section} - live route context loaded`, status: 'Ready', icon: 'bi-grid', tone: 'success' },
    { title: 'AI prompts available', meta: 'Suggested questions adapt to this page', status: 'Live', icon: 'bi-stars', tone: 'neutral' },
    { title: 'Follow-up queue', meta: 'Use the panel to focus the next action', status: 'Open', icon: 'bi-arrow-repeat', tone: 'warning' }
  ],
  input_placeholder: `Ask about ${title.toLowerCase()}, entities, gates, or templates...`
});

const build_assigned_form_content = (context) => {
  const { section, title, case_name, company_name } = context;
  const target_label = case_name || company_name || 'this onboarding entity';
  const intro = company_name
    ? `${target_label} is open in the assigned form workspace for ${company_name}.`
    : `${target_label} is open in the assigned form workspace.`;

  return {
    summary_label: 'Briefing',
    summary_intro: intro,
    briefing_body: `Capture the missing answers for ${target_label}, validate every mandatory section, and use the supporting evidence to move the application forward without rework.`,
    briefing_meta: build_application_meta(context),
    suggested_questions: [
      `What is still missing for ${target_label}?`,
      `Which sections are mandatory for ${target_label}?`,
      company_name ? `What documents should ${company_name} upload next?` : 'What evidence should be uploaded next?',
      'What could block submission on this form?',
      'What should I review before saving this form?'
    ],
    recent_evidence: [
      { title: 'Assigned form in progress', meta: `${target_label} - responses being completed`, status: 'Open', icon: 'bi-pencil-square', tone: 'warning' },
      { title: 'Mandatory sections loaded', meta: `${section} - ${title}`, status: 'Ready', icon: 'bi-list-check', tone: 'success' },
      { title: 'Submission path available', meta: 'Save once all required fields are complete', status: 'Live', icon: 'bi-send-check', tone: 'neutral' }
    ],
    input_placeholder: `Ask about ${case_name || 'this entity'}, its form, or required evidence...`
  };
};

const build_manage_verification_content = ({ section, title }) => ({
  summary_label: 'Briefing',
  summary_intro: 'Verification activity is centralized here so teams can clear blockers before activation.',
  briefing_body: 'Review outstanding checks, inspect evidence readiness, and move directly from a flagged verification item into the assigned form when an entity needs remediation.',
  briefing_meta: `Route - ${section} / ${title} - Verification workspace`,
  suggested_questions: [
    'Which verification checks are still blocked?',
    'Which entity should be opened first?',
    'What evidence is overdue right now?',
    'Which forms still need remediation?',
    'What can be activated after verification closes?'
  ],
  recent_evidence: [
    { title: 'Verification queue', meta: 'Open checks are ready for review', status: 'Live', icon: 'bi-shield-check', tone: 'neutral' },
    { title: 'Evidence follow-up', meta: 'Jump from failed checks into the related form', status: 'Action', icon: 'bi-arrow-up-right-circle', tone: 'warning' },
    { title: 'Entity detail hydration', meta: 'Summary, profile and questionnaire context available', status: 'Ready', icon: 'bi-card-checklist', tone: 'success' }
  ],
  input_placeholder: 'Ask about any verification blocker, entity, or evidence item...'
});

const build_manage_manual_verification_content = ({ section, title }) => ({
  summary_label: 'Briefing',
  summary_intro: 'Manual verification is handled in a separate reviewer workspace for onboarded entities.',
  briefing_body: 'Open each case that needs human review, inspect the submitted answers and supporting evidence, capture field-by-field findings, and then record the final reviewer decision.',
  briefing_meta: `Route - ${section} / ${title} - Manual verification workspace`,
  suggested_questions: [
    'Which onboarded entities need manual review first?',
    'Which manual checks are overdue right now?',
    'What evidence is still missing for manual verification?',
    'Which cases are ready for a final reviewer decision?',
    'Which manual findings still need to be captured?'
  ],
  recent_evidence: [
    { title: 'Manual review queue', meta: 'Reviewer-driven checks are separated from automated verification', status: 'Live', icon: 'bi-shield', tone: 'warning' },
    { title: 'Field capture enabled', meta: 'Per-field manual outcomes can be recorded before final decision', status: 'Ready', icon: 'bi-pencil-square', tone: 'success' },
    { title: 'Onboarding answers linked', meta: 'Submitted form answers are available inside each manual review case', status: 'Ready', icon: 'bi-card-checklist', tone: 'neutral' }
  ],
  input_placeholder: 'Ask about any manual verification case, evidence item, or reviewer action...'
});

const build_verification_case_content = (context) => {
  const { company_name } = context;
  const target_label = context.case_name || company_name || 'this onboarding entity';
  const intro = company_name
    ? `${target_label} is open in the verification workspace for ${company_name}.`
    : `${target_label} is open in the verification workspace.`;

  return {
    summary_label: 'Briefing',
    summary_intro: intro,
    briefing_body: `Run the required verification types one by one for ${target_label}, review each result as it returns, and move into the assigned form only when remediation is needed.`,
    briefing_meta: build_application_meta(context),
    suggested_questions: [
      `Which verification should I run first for ${target_label}?`,
      'What result came back from the latest verification run?',
      'Which failed verifications need remediation?',
      'What evidence is still missing for this case?',
      'When is this case ready to move forward?'
    ],
    recent_evidence: [
      { title: 'Verification case open', meta: `${target_label} - per-check execution available`, status: 'Open', icon: 'bi-shield-check', tone: 'warning' },
      { title: 'Results refresh enabled', meta: 'Each verification returns directly into this case view', status: 'Ready', icon: 'bi-arrow-repeat', tone: 'success' },
      { title: 'Assigned form linked', meta: 'Open the form only when follow-up is required', status: 'Live', icon: 'bi-box-arrow-up-right', tone: 'neutral' }
    ],
    input_placeholder: `Ask about ${target_label}, verification results, or next actions...`
  };
};

const build_manual_verification_case_content = (context) => {
  const { company_name } = context;
  const target_label = context.case_name || company_name || 'this onboarding entity';
  const intro = company_name
    ? `${target_label} is open in the manual verification workspace for ${company_name}.`
    : `${target_label} is open in the manual verification workspace.`;

  return {
    summary_label: 'Briefing',
    summary_intro: intro,
    briefing_body: `Review the required manual verification items for ${target_label}, capture field-level findings from the submitted onboarding answers and external evidence, then record the final decision when the review is complete.`,
    briefing_meta: build_application_meta(context),
    suggested_questions: [
      `Which manual verification should I complete first for ${target_label}?`,
      'Which submitted answers still need reviewer confirmation?',
      'What evidence is missing for this manual review?',
      'Which field findings have already been captured?',
      'Is this case ready for a final manual decision?'
    ],
    recent_evidence: [
      { title: 'Manual verification case open', meta: `${target_label} - reviewer workflow available`, status: 'Open', icon: 'bi-shield', tone: 'warning' },
      { title: 'Field review capture', meta: 'Record match, mismatch, partial, or unable-to-verify results', status: 'Ready', icon: 'bi-pencil-square', tone: 'success' },
      { title: 'Decision close-out', meta: 'Save the overall manual verification outcome when review is complete', status: 'Live', icon: 'bi-check2-circle', tone: 'neutral' }
    ],
    input_placeholder: `Ask about ${target_label}, manual findings, or reviewer next actions...`
  };
};

const PANEL_CONTENT_BUILDERS = new Map([
  ['MyQueueForm', build_assigned_form_content],
  ['OnboardingManageVerification', build_manage_verification_content],
  ['OnboardingManageManualVerification', build_manage_manual_verification_content],
  ['OnboardingVerificationCase', build_verification_case_content],
  ['OnboardingManualVerificationCase', build_manual_verification_case_content]
]);

export const build_onboarding_ai_panel_content = (route) => {
  const context = get_route_label_context(route);
  const builder = PANEL_CONTENT_BUILDERS.get(context.route_name) || build_default_panel_content;
  return builder(context);
};
